import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWallet,
  faShoppingCart,
  faGift,
  faPiggyBank,
  faExchangeAlt,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import BottomMenuMobile from "../components/BottomMenuMobile";

function MainMenu({ accounts = [], savingAccounts = [] }) {
  const { t } = useTranslation();
  const [dailyNorm, setDailyNorm] = useState(null);
  const [monthlyPayments, setMonthlyPayments] = useState({
    paid: 0,
    total: 0,
    totalEur: 0,
    unpaidEur: 0,
  });
  const [loading, setLoading] = useState(true);
  const [rates, setRates] = useState(null);

  const convertToEur = (amount, currency, rates) => {
    if (!rates || !amount) return Number(amount) || 0;
    if (currency === "EUR") return Number(amount);

    const rate = rates[currency];
    if (!rate) return Number(amount); // Fallback if rate missing

    return Number(amount) / rate;
  };

  const [totalSavings, setTotalSavings] = useState(0);

  // Calculate total wealth
  const totalAccounts = accounts.reduce((sum, acc) => {
    return sum + convertToEur(acc.balance, acc.currency, rates);
  }, 0);

  const totalWealth = totalAccounts + totalSavings;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [burndownRes, monthlyRes, ratesRes, savingsRes] =
          await Promise.allSettled([
            axios.get("/api/burndown-chart/latest"),
            axios.get(`${process.env.REACT_APP_API_URL}/monthly-payments`),
            axios.get(`${process.env.REACT_APP_API_URL}/exchange-rates`),
            axios.get(`${process.env.REACT_APP_API_URL}/total-saving-balance`),
          ]);

        if (ratesRes.status === "fulfilled" && ratesRes.value.data?.rates) {
          setRates(ratesRes.value.data.rates);
        }

        if (savingsRes.status === "fulfilled" && savingsRes.value.data) {
          setTotalSavings(savingsRes.value.data.totalSavingBalance || 0);
        }

        if (burndownRes.status === "fulfilled" && burndownRes.value.data) {
          const { plannedSpending } = burndownRes.value.data;
          if (plannedSpending && plannedSpending.length >= 2) {
            const start = plannedSpending[0].balance;
            const end = plannedSpending[plannedSpending.length - 1].balance;
            const days = plannedSpending.length - 1;
            const norm = days > 0 ? (start - end) / days : 0;
            setDailyNorm(norm.toFixed(2));
          } else {
            setDailyNorm(null);
          }
        }

        if (monthlyRes.status === "fulfilled" && monthlyRes.value.data) {
          const payments = monthlyRes.value.data;
          const activePayments = payments.filter((p) => !p.archived);
          const paidCount = activePayments.filter((p) => p.paid).length;

          // Get rates for EUR calculation
          const fetchedRates =
            ratesRes.status === "fulfilled" ? ratesRes.value.data?.rates : null;

          // Calculate EUR totals
          const calcEur = (amount, currency) => {
            if (!fetchedRates || !amount) return Number(amount) || 0;
            if (currency === "EUR") return Number(amount);
            const rate = fetchedRates[currency];
            if (!rate) return Number(amount);
            return Number(amount) / rate;
          };

          const totalEur = activePayments.reduce(
            (sum, p) => sum + calcEur(p.amount, p.currency),
            0
          );
          const unpaidEur = activePayments
            .filter((p) => !p.paid)
            .reduce((sum, p) => sum + calcEur(p.amount, p.currency), 0);

          setMonthlyPayments({
            paid: paidCount,
            total: activePayments.length,
            totalEur,
            unpaidEur,
          });
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const quickActions = [
    {
      to: "/balance",
      icon: faWallet,
      label: t("balance", "Баланс"),
      color: "bg-blue-500",
      shadow: "shadow-blue-500/30",
      bg: "bg-blue-100 dark:bg-blue-900/30",
      textColor: "text-blue-500 dark:text-blue-400",
    },
    {
      to: "/shopping-list",
      icon: faShoppingCart,
      label: t("shoppingList", "Список покупок"),
      color: "bg-emerald-500",
      shadow: "shadow-emerald-500/30",
      bg: "bg-emerald-100 dark:bg-emerald-900/30",
      textColor: "text-emerald-500 dark:text-emerald-400",
    },
    {
      to: "/wish-list",
      icon: faGift,
      label: t("wishList", "Wishlist"),
      color: "bg-purple-500",
      shadow: "shadow-purple-500/30",
      bg: "bg-purple-100 dark:bg-purple-900/30",
      textColor: "text-purple-500 dark:text-purple-400",
    },
    {
      to: "/savings",
      icon: faPiggyBank,
      label: t("savings", "Скарбничка"),
      color: "bg-amber-500",
      shadow: "shadow-amber-500/30",
      bg: "bg-amber-100 dark:bg-amber-900/30",
      textColor: "text-amber-500 dark:text-amber-400",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-200">
      <div className="space-y-6 pb-32 lg:pb-8 max-w-5xl mx-auto">
        {/* Header */}
        <header className="flex justify-between items-center"></header>

        {/* Total Wealth Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative overflow-hidden bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 dark:from-primary-600 dark:via-primary-700 dark:to-primary-800 rounded-2xl p-6 text-white shadow-xl shadow-primary-500/25 dark:shadow-primary-900/30"
        >
          {/* Subtle decorative circles */}
          <div className="absolute -top-12 -right-12 w-40 h-40 bg-white/10 rounded-full" />
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-white/5 rounded-full" />

          {/* Content */}
          <div className="relative">
            <p className="text-white/70 text-sm font-medium mb-1">
              {t("totalCapital", "Загальний капітал")}
            </p>
            <h2 className="text-4xl font-bold tracking-tight text-white mb-5">
              {totalWealth.toLocaleString(undefined, {
                maximumFractionDigits: 0,
              })}{" "}
              <span className="text-xl font-normal text-white/70">€</span>
            </h2>

            {/* Stats row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <FontAwesomeIcon
                    icon={faWallet}
                    className="text-white/70 text-xs"
                  />
                  <span className="text-xs text-white/70">
                    {t("accounts", "Рахунки")}
                  </span>
                </div>
                <p className="text-lg font-semibold text-white">
                  {totalAccounts.toLocaleString(undefined, {
                    maximumFractionDigits: 0,
                  })}
                  <span className="text-sm font-normal text-white/60 ml-1">
                    €
                  </span>
                </p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <FontAwesomeIcon
                    icon={faPiggyBank}
                    className="text-white/70 text-xs"
                  />
                  <span className="text-xs text-white/70">
                    {t("savings", "Скарбничка")}
                  </span>
                </div>
                <p className="text-lg font-semibold text-white">
                  {totalSavings.toLocaleString(undefined, {
                    maximumFractionDigits: 0,
                  })}
                  <span className="text-sm font-normal text-white/60 ml-1">
                    €
                  </span>
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">
            {t("quickActions", "Швидкі дії")}
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <Link key={index} to={action.to}>
                <motion.div
                  whileTap={{ scale: 0.95 }}
                  className="p-4 rounded-xl bg-white dark:bg-dark-surface shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col items-center justify-center gap-3 h-32 transition-all hover:shadow-md"
                >
                  <div
                    className={`w-12 h-12 rounded-full ${action.bg} flex items-center justify-center text-xl shadow-sm`}
                  >
                    <FontAwesomeIcon
                      icon={action.icon}
                      className={action.textColor}
                    />
                  </div>
                  <span className="font-medium text-gray-700 dark:text-gray-200 text-sm">
                    {action.label}
                  </span>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 gap-4">
          {/* Monthly Payments Widget - always full width on mobile */}
          <Link
            to="/monthly-payments"
            className={
              dailyNorm === null ? "col-span-2" : "col-span-2 lg:col-span-1"
            }
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-dark-surface p-3 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 h-full transition-colors"
            >
              {/* Header row: icon + title on left, total on right */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 flex items-center justify-center">
                    <FontAwesomeIcon icon={faExchangeAlt} />
                  </div>
                  <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">
                    {t("monthlyPayments", "Щомісячні платежі")}
                  </h4>
                </div>
                {!loading && (
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                      {monthlyPayments.totalEur.toFixed(0)} €
                    </span>
                    <span className="text-xs text-gray-400">
                      {t("total", "всього")}
                    </span>
                  </div>
                )}
              </div>
              {/* Status row */}
              {loading ? (
                <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
              ) : (
                <div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
                    <span className="text-pink-600 dark:text-pink-400 font-medium">
                      {monthlyPayments.unpaidEur.toFixed(0)} €
                    </span>
                    <span>{t("unpaid", "не сплачено")}</span>
                    <span className="text-gray-300 dark:text-gray-600">•</span>
                    <span>
                      {monthlyPayments.paid}/{monthlyPayments.total}{" "}
                      {t("paid", "сплачено")}
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-pink-500 h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${
                          monthlyPayments.total
                            ? (monthlyPayments.paid / monthlyPayments.total) *
                              100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
              )}
            </motion.div>
          </Link>

          {/* Daily Norm Widget */}
          {dailyNorm !== null && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-dark-surface p-3 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 transition-colors"
            >
              <div className="flex flex-col items-start gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                  <FontAwesomeIcon icon={faChartLine} />
                </div>
                <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300 leading-tight">
                  {t("dailyNorm", "Денна норма")}
                </h4>
              </div>
              <div className="mt-2">
                {loading ? (
                  <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                ) : (
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {dailyNorm}{" "}
                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                      EUR
                    </span>
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Bottom menu shown on this page to keep consistency with other screens - hidden on desktop */}
      <div className="lg:hidden">
        <BottomMenuMobile />
      </div>
    </div>
  );
}

export default MainMenu;
